import { baseCSS, openPrint, fmt } from './workflowPDF';
import { MARK } from '../constants';
import { identidadeDoLaboratorio } from './laboratorioNoRelatorio';
import { dataDaRetirada } from '../constants/workflowGranular';
import { localDoServico } from '../data/clients';

/**
 * Os oito relatórios de visita do programa SEBRAE.
 *
 * São oito documentos e UM gerador porque eles diferem em cinco coisas —
 * título, subtítulo, rótulo das atividades, título das recomendações e a
 * tabela de animais — e são idênticos em todo o resto: cabeçalho de seis
 * campos, bloco técnico e assinatura do produtor. Oito funções seriam oito
 * cópias do mesmo rodapé, e sete delas ficariam desatualizadas no dia em que
 * o texto mudasse.
 *
 * `montarRelatorioHTML` é PURA: recebe o contexto e devolve string. É o que
 * permite testar o documento sem navegador — e o conteúdo deste documento é
 * assinado pelo produtor, então errar nele custa caro.
 */

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Campo ausente vira travessão, nunca "null" ou "undefined" impresso. */
const ou = (v) => {
  const t = String(v ?? '').trim();
  return t || '—';
};

// ── De onde sai cada campo do cabeçalho ──────────────────────────────────

/**
 * Neste cadastro `clients.name` É a fazenda e `contact` é a pessoa — o rótulo
 * do campo é literalmente "Nome da fazenda". Trocar os dois poria a fazenda na
 * linha da assinatura dos oito relatórios.
 */
export const fazendaDoContexto = (ctx, sessao) =>
  ou(sessao?.farm
    // Cliente com mais de uma fazenda: o serviço diz em qual foi.
    || (ctx?.service?.local_id && localDoServico(ctx.service, ctx.client)?.nome)
    || ctx?.service?.client_name || ctx?.client?.farm_name);

export const produtorDoContexto = (ctx) =>
  ou(ctx?.client?.contact || ctx?.client?.name);

/**
 * CONSULTOR(A): o responsável pelo projeto SEBRAE do laboratório, em TODOS os
 * relatórios — quem responde pelo projeto perante o programa, não
 * necessariamente quem foi à fazenda. O técnico da sessão só entra quando o
 * laboratório não tem responsável cadastrado.
 */
const responsavelDoProjeto = (ctx) => {
  const lab = ctx?.laboratory;
  return lab?.sebrae ? String(lab.sebrae_responsavel || '').trim() : '';
};

export const consultorDoContexto = (ctx, sessao) =>
  ou(responsavelDoProjeto(ctx) || sessao?.technician_name);

/**
 * Responsável de cada etapa do cronograma: o mesmo consultor do cabeçalho.
 * O que foi digitado na linha só vale sem responsável pelo projeto.
 */
export const responsavelDaEtapa = (ctx, aplicacao) =>
  ou(responsavelDoProjeto(ctx) || aplicacao?.responsavel);

export const municipioDoContexto = (ctx, sessao) => {
  const cidade = sessao?.city || ctx?.client?.city;
  const uf = ctx?.client?.state;
  if (!cidade) return '—';
  return uf ? `${cidade} — ${uf}` : String(cidade);
};

// ── Texto fixo, copiado dos modelos ──────────────────────────────────────

const RECOMENDACOES = [
  { titulo: 'MANEJO', linhas: [
    'Retirar o touro do contato com as receptoras.',
    'Evitar manejos que estressem os animais.',
  ] },
  { titulo: 'NUTRIÇÃO', linhas: [
    'Evitar alterações bruscas na dieta nutricional atual da fazenda.',
    'Fornecer sal mineral.',
  ] },
  { titulo: 'SANIDADE', linhas: [
    'Vacina só poderá ser aplicada quando o médico veterinário autorizar.',
  ] },
];

const FECHAMENTO = [
  'Estas orientações devem ser seguidas ao longo dos serviços da FIV.',
  'Se dúvidas persistirem após as orientações, o produtor deverá fazer contato '
  + 'imediato com o médico veterinário da prestadora de serviços para que as '
  + 'decisões com relação a manejo, nutrição e sanidade sejam tomadas.',
];

/** Só nos relatórios de doadora: prende as receptoras até a sexagem. */
const RECOMENDACAO_SEXAGEM =
  'As receptoras devem permanecer na fazenda até o diagnóstico de sexagem.';

const CIENCIA = 'ESTOU CIENTE QUE UMA VIA DO RELATÓRIO DE VISITA FICOU NA '
  + 'PROPRIEDADE PARA ACOMPANHAMENTO DOS SERVIÇOS';

// ── Leitores do contexto ─────────────────────────────────────────────────

const receptorasAtivas = (ctx) =>
  (ctx?.protocolo?.receptoras || []).filter(r => r.ativa !== false);

const doadorasDaOPU = (ctx) => (ctx?.opu?.donors || [])
  .filter(d => d.animal_identifier || d.animal_id);

/** OPU herdada sem doadora casada = nada foi transferido aqui ainda. */
const faltaDoadora = (ctx) => {
  if (doadorasDaOPU(ctx).length > 0) return [];
  return ctx?.opu?.herdada
    ? ['OPU de outro serviço: nenhum embrião dela transferido neste serviço']
    : ['OPU sem doadoras lançadas'];
};

const transferenciasAprovadas = (ctx) =>
  (ctx?.te?.transfers || []).filter(t => t.status === 'APROVADA');

const oocitosViaveis = (ctx) => doadorasDaOPU(ctx)
  .reduce((s, d) => s + (parseInt(d.oocytes_viable) || 0), 0);

const prenhesDe = (checks = []) => checks.filter(c => c.result === 'PRENHE');

/** Prenhe, vazia ou inconclusiva — com a marca, que imprime em preto e branco. */
const marcaDoResultado = (resultado) => {
  if (resultado === 'PRENHE') return `${MARK.OK} P`;
  if (resultado === 'VAZIA' || resultado === 'ABORTOU') return `${MARK.BAD} F`;
  return `${MARK.UNKNOWN} —`;
};

// ── A spec dos oito ──────────────────────────────────────────────────────

/**
 * Cada entrada diz só o que a DIFERENCIA das outras sete.
 *
 * `etapa`                  completa o título "RELATÓRIO DE VISITA n ..."
 * `subtitulo`              segunda linha, só nos de doadora
 * `atividadeNoSingular`    "ATIVIDADE DESENVOLVIDA:", como os de doadora usam
 * `recomendacaoAteProxima` muda o título do bloco técnico, idem
 * `sessaoDo`               de onde saem data, consultor, fazenda e município
 * `etapaDoServico`         chave da etapa no contexto; só gera se ela estiver concluída
 * `tabela`                 colunas e linhas da lista de animais, ou ausente
 * `atividadesPadrao`       texto calculado, quando não há texto gravado
 * `requisitos`             o que falta para o relatório poder existir
 */
export const RELATORIOS_DE_VISITA = [
  {
    numero: 1,
    etapa: 'SELEÇÃO RECEPTORAS',
    sessaoDo: (ctx) => ctx?.protocolo?.session,
    etapaDoServico: 'protocolo',
    cronograma: 'todas',
    tabela: {
      colunas: ['#', 'Receptora'],
      paresPorLinha: 2,
      linhas: (ctx) => receptorasAtivas(ctx).map((r, i) => [i + 1, ou(r.identifier)]),
    },
    atividadesPadrao: (ctx) => [
      'INÍCIO DE PROTOCOLO HORMONAL',
      'Número de embriões contratados: ' + ou(ctx?.service?.embrioes_contratados),
      'Número de receptoras protocoladas: ' + receptorasAtivas(ctx).length,
    ].join('\n'),
    observacoesPadrao: (ctx) =>
      'FORAM PROTOCOLADAS ' + receptorasAtivas(ctx).length + ' RECEPTORAS APTAS',
    requisitos: (ctx) => receptorasAtivas(ctx).length === 0
      ? ['Etapa de protocolo sem receptoras lançadas'] : [],
  },
  {
    numero: 2,
    etapa: 'INICIO DE PROTOCOLO HORMONAL',
    sessaoDo: (ctx) => ctx?.protocolo?.session,
    etapaDoServico: 'protocolo',
    cronograma: 'todas',
    tabela: {
      colunas: ['#', 'Receptora'],
      paresPorLinha: 2,
      linhas: (ctx) => receptorasAtivas(ctx).map((r, i) => [i + 1, ou(r.identifier)]),
    },
    atividadesPadrao: (ctx) => [
      'INÍCIO DE PROTOCOLO HORMONAL',
      'Número de embriões contratados: ' + ou(ctx?.service?.embrioes_contratados),
      'Número de receptoras protocoladas: ' + receptorasAtivas(ctx).length,
    ].join('\n'),
    observacoesPadrao: (ctx) =>
      'FORAM PROTOCOLADAS ' + receptorasAtivas(ctx).length + ' RECEPTORAS APTAS',
    requisitos: (ctx) => receptorasAtivas(ctx).length === 0
      ? ['Etapa de protocolo sem receptoras lançadas'] : [],
  },
  {
    numero: 3,
    etapa: 'SELEÇÃO DE DOADORAS',
    subtitulo: 'MODALIDADE L E PSL',
    atividadeNoSingular: true,
    recomendacaoAteProxima: true,
    sessaoDo: (ctx) => ctx?.opu?.session,
    etapaDoServico: 'opu',
    atividadesPadrao: (ctx) => {
      const nomes = doadorasDaOPU(ctx).map(d => d.animal_identifier).filter(Boolean);
      return [
        'FORAM SELECIONADAS ' + nomes.length + ' doadoras, abaixo relacionadas, '
          + 'juntamente com o produtor: ' + (nomes.join(', ') || '—') + '.',
        'Foram selecionadas ' + nomes.length + ' doadoras para acasalamento com '
          + 'touros definidos para produção de '
          + ou(ctx?.service?.embrioes_contratados) + ' EMBRIÕES.',
        'O relatório de aspiração e produção de embriões será disponibilizado ao '
          + 'produtor junto à documentação final.',
      ].join('\n\n');
    },
    observacoesPadrao: (ctx) =>
      'FORAM SELECIONADAS ' + doadorasDaOPU(ctx).length
      + ' DOADORAS PARA ASPIRAÇÃO FOLICULAR',
    requisitos: faltaDoadora,
  },
  {
    numero: 4,
    etapa: 'RETIRADA DE PROTOCOLO',
    sessaoDo: (ctx) => ctx?.protocolo?.session,
    etapaDoServico: 'protocolo',
    // A visita de retirada e SETE DIAS depois do implante. Datar pela sessao
    // poria o dia do implante num documento que descreve outro dia -- e o
    // produtor assina a data impressa.
    dataPadrao: (ctx) => dataDaRetirada(
      ctx?.protocolo?.aplicacoes, ctx?.protocolo?.session?.date),
    cronograma: 'retirada',
    tabela: {
      colunas: ['#', 'Receptora'],
      paresPorLinha: 2,
      linhas: (ctx) => receptorasAtivas(ctx).map((r, i) => [i + 1, ou(r.identifier)]),
    },
    atividadesPadrao: () => 'FORAM RETIRADOS OS PROTOCOLOS DAS RECEPTORAS ABAIXO.',
    observacoesPadrao: (ctx) =>
      'FORAM RETIRADOS ' + receptorasAtivas(ctx).length + ' PROTOCOLOS',
    requisitos: (ctx) => receptorasAtivas(ctx).length === 0
      ? ['Etapa de protocolo sem receptoras lançadas'] : [],
  },
  {
    numero: 5,
    etapa: 'ASPIRAÇÃO FOLICULAR',
    subtitulo: 'MODALIDADE L E PSL',
    atividadeNoSingular: true,
    recomendacaoAteProxima: true,
    sessaoDo: (ctx) => ctx?.opu?.session,
    etapaDoServico: 'opu',
    atividadesPadrao: (ctx) => {
      const nomes = doadorasDaOPU(ctx).map(d => d.animal_identifier).filter(Boolean);
      return [
        'FORAM ASPIRADAS ' + nomes.length + ' doadoras, abaixo relacionadas, '
          + 'juntamente com o produtor: ' + (nomes.join(', ') || '—') + '.',
        'Foram aspiradas ' + nomes.length + ' doadoras para acasalamento com touros '
          + 'definidos para produção de ' + ou(ctx?.service?.embrioes_contratados)
          + ' EMBRIÕES, com ' + oocitosViaveis(ctx) + ' OÓCITOS VIÁVEIS.',
        'O relatório de aspiração e produção de embriões será disponibilizado ao '
          + 'produtor junto à documentação final.',
      ].join('\n\n');
    },
    observacoesPadrao: (ctx) =>
      'FORAM ASPIRADAS ' + doadorasDaOPU(ctx).length + ' DOADORAS COM '
      + oocitosViaveis(ctx) + ' OÓCITOS VIÁVEIS',
    requisitos: faltaDoadora,
  },
  {
    numero: 6,
    etapa: 'TRANSFERENCIA DE EMBRIOES',
    sessaoDo: (ctx) => ctx?.te?.session,
    etapaDoServico: 'te',
    tabela: {
      colunas: ['#', 'Receptora', 'Embrião'],
      paresPorLinha: 2,
      linhas: (ctx) => transferenciasAprovadas(ctx).map((t, i) => [
        i + 1,
        ou(t.recipient?.identifier || t.recipient_identifier),
        ou(t.embryo_number),
      ]),
    },
    atividadesPadrao: (ctx) =>
      'FORAM TRANSFERIDOS ' + transferenciasAprovadas(ctx).length
      + ' EMBRIÕES NAS RECEPTORAS ABAIXO RELACIONADAS.',
    observacoesPadrao: (ctx) =>
      'FORAM TRANSFERIDOS ' + transferenciasAprovadas(ctx).length + ' EMBRIÕES',
    requisitos: (ctx) => transferenciasAprovadas(ctx).length === 0
      ? ['TE sem transferência aprovada'] : [],
  },
  {
    numero: 7,
    etapa: 'DIAGNOSTICO PRECOCE',
    sessaoDo: (ctx) => ctx?.dg30?.session,
    etapaDoServico: 'dg30',
    tabela: {
      colunas: ['#', 'Receptora', 'Resultado'],
      paresPorLinha: 2,
      linhas: (ctx) => (ctx?.dg30?.checks || []).map((c, i) => [
        i + 1, ou(c.recipient_identifier), marcaDoResultado(c.result),
      ]),
    },
    atividadesPadrao: () => 'FORAM DIAGNOSTICADAS AS RECEPTORAS ABAIXO RELACIONADAS.',
    observacoesPadrao: (ctx) =>
      'FORAM DIAGNOSTICADAS ' + prenhesDe(ctx?.dg30?.checks).length + ' PRENHES',
    requisitos: (ctx) => (ctx?.dg30?.checks || []).length === 0
      ? ['DG 30 dias sem diagnóstico lançado'] : [],
  },
  {
    numero: 8,
    etapa: 'DIAGNOSTICO DEFINITIVO',
    sessaoDo: (ctx) => ctx?.dg60?.session,
    etapaDoServico: 'dg60',
    tabela: {
      colunas: ['#', 'Receptora', 'Resultado', 'Sexo'],
      paresPorLinha: 1,
      // Só as prenhes. Vazia entraria com o sexo em branco e viraria linha
      // muda num documento que o produtor assina.
      linhas: (ctx) => prenhesDe(ctx?.dg60?.checks).map((c, i) => [
        i + 1, ou(c.recipient_identifier), `${MARK.OK} P`, ou(c.sexing?.sex),
      ]),
    },
    atividadesPadrao: () => 'FORAM DIAGNOSTICADAS AS PRENHES ABAIXO RELACIONADAS.',
    observacoesPadrao: (ctx) =>
      'FORAM DIAGNOSTICADAS ' + prenhesDe(ctx?.dg60?.checks).length + ' PRENHES',
    requisitos: (ctx) => {
      const prenhes = prenhesDe(ctx?.dg60?.checks);
      if (prenhes.length === 0) return ['DG 60 dias sem prenhez confirmada'];
      if (prenhes.every(c => !c.sexing?.sex)) return ['Sexagem não lançada no DG 60'];
      return [];
    },
  },
];

export const RELATORIO_POR_NUMERO =
  Object.fromEntries(RELATORIOS_DE_VISITA.map(r => [r.numero, r]));

// ── Montagem do documento ────────────────────────────────────────────────

/**
 * O que IMPEDE o relatório de existir. Vazio = pode gerar.
 *
 * Só entra aqui o que não tem como sair no papel: etapa não lançada não tem
 * lista para imprimir. Campo de cabeçalho em branco não impede — sai como
 * travessão e o consultor completa à mão, que é melhor que não ter o
 * documento.
 */
const NOME_DA_ETAPA = {
  protocolo: 'Protocolo', opu: 'OPU', te: 'TE', dg30: 'DG 30', dg60: 'DG 60',
};

export function requisitosDoRelatorio(numero, ctx) {
  const spec = RELATORIO_POR_NUMERO[numero];
  if (!spec) return ['Relatório desconhecido'];
  const faltas = spec.requisitos ? spec.requisitos(ctx) : [];
  // Só sai relatório de etapa CONCLUÍDA: o documento é assinado pelo produtor,
  // e uma etapa em andamento ainda pode mudar a lista que ele assinou.
  const etapa = spec.etapaDoServico && ctx?.[spec.etapaDoServico];
  if (etapa && !etapa.completed) {
    faltas.push(`${NOME_DA_ETAPA[spec.etapaDoServico]} não concluída`);
  }
  return faltas;
}

/**
 * A OPU que entra nos relatórios de um serviço que HERDOU a OPU ou a FIV.
 *
 * A coleta foi lançada no serviço de origem, muitas vezes de outro cliente,
 * com doadoras que abasteceram vários serviços (a 0085 aspirou 27 e cedeu
 * para seis). Este serviço só responde pelas que geraram os embriões
 * transferidos NELE — casadas pelo animal da doadora de cada embrião.
 *
 * Sem TE lançada não há o que casar, e a lista sai vazia: o relatório trava
 * em vez de imprimir as doadoras de outro produtor. A sessão (data, técnico)
 * continua a da origem, que é quando a coleta aconteceu; a fazenda sai,
 * porque o cabeçalho é do cliente deste serviço.
 */
export function opuHerdada(opuDaOrigem, animaisTransferidos = []) {
  if (!opuDaOrigem) return null;
  const usados = new Set(animaisTransferidos.filter(Boolean));
  const { farm, city, ...session } = opuDaOrigem.session || {};
  return {
    ...opuDaOrigem,
    session,
    herdada: true,
    donors: (opuDaOrigem.donors || []).filter(d => d.animal_id && usados.has(d.animal_id)),
  };
}

/**
 * O que vai sair em branco no cabeçalho, e onde se preenche.
 *
 * O formulário do SEBRAE tem seis campos no topo e espera os seis. O sistema
 * tem todos eles — mas `cidade`, `UF` e `contato` são opcionais no cadastro do
 * cliente, e ficam vazios sem que nada reclame até o dia da impressão.
 *
 * Avisar é diferente de bloquear: o documento sai com travessão e assinado à
 * mão, e isso é melhor que não sair.
 */
export function avisosDoCabecalho(ctx) {
  const avisos = [];
  if (!ctx?.service?.sebrae_projeto) {
    avisos.push('Nº do projeto — preencha no cadastro do serviço');
  }
  if (ctx?.laboratory?.sebrae && !String(ctx.laboratory.sebrae_responsavel || '').trim()) {
    avisos.push('Consultor(a) — sem responsável pelo projeto no cadastro do laboratório; sai o técnico da etapa');
  }
  if (!ctx?.client?.contact) {
    avisos.push('Produtor — "Nome do contato" no cadastro do cliente');
  }
  if (!ctx?.client?.city) {
    avisos.push('Município — "Cidade" no cadastro do cliente');
  }
  return avisos;
}

/** A DATA do cabeçalho: a gravada à mão vence a da sessão. */
export function dataDoRelatorio(numero, ctx) {
  const gravado = ctx?.textos?.[numero]?.data_visita;
  if (gravado) return gravado;
  const spec = RELATORIO_POR_NUMERO[numero];
  // Quase todos são datados pela sessão da etapa. A retirada não: ela é outra
  // visita, sete dias depois, e a data dela sai do cronograma.
  return spec?.dataPadrao?.(ctx) || spec?.sessaoDo?.(ctx)?.date || null;
}

const linhaDeCabecalho = (rotulo, valor) => `
  <tr><td class="rot">${esc(rotulo)}</td><td>${esc(valor)}</td></tr>`;

function cabecalho6Campos(numero, ctx, sessao) {
  return `
  <table class="cabecalho bloco">
    ${linhaDeCabecalho('DATA', fmt(dataDoRelatorio(numero, ctx)))}
    ${linhaDeCabecalho('CONSULTOR(A)', consultorDoContexto(ctx, sessao))}
    ${linhaDeCabecalho('FAZENDA', fazendaDoContexto(ctx, sessao))}
    ${linhaDeCabecalho('PRODUTOR(A)', produtorDoContexto(ctx))}
    ${linhaDeCabecalho('MUNICÍPIO', municipioDoContexto(ctx, sessao))}
    ${linhaDeCabecalho('Nº PROJETO', ou(ctx?.service?.sebrae_projeto))}
  </table>`;
}

/**
 * A lista de animais, em blocos lado a lado.
 *
 * Trinta receptoras numa coluna só empurram as recomendações e a assinatura
 * para a segunda folha, e assinatura órfã é ruim em documento assinado.
 */
function tabelaDeLista({ colunas, linhas, paresPorLinha = 1 }) {
  if (!linhas.length) return '';
  const porLinha = Math.max(1, paresPorLinha);
  const cabecalho = Array.from({ length: porLinha })
    .flatMap(() => colunas).map(c => `<th>${esc(c)}</th>`).join('');

  const corpo = [];
  for (let i = 0; i < linhas.length; i += porLinha) {
    const bloco = linhas.slice(i, i + porLinha);
    const celulas = bloco.flatMap(l => l.map(v => `<td>${esc(v)}</td>`));
    // Completa o último bloco para as bordas fecharem.
    while (celulas.length < porLinha * colunas.length) celulas.push('<td></td>');
    corpo.push(`<tr>${celulas.join('')}</tr>`);
  }

  return `<table class="lista"><thead><tr>${cabecalho}</tr></thead>
    <tbody>${corpo.join('')}</tbody></table>`;
}

function tabelaCronograma(aplicacoes = [], ctx) {
  if (!aplicacoes.length) return '';
  const linhas = aplicacoes.map(a => `<tr>
    <td>${esc(fmt(a.data))}</td><td>${esc(ou(a.atividade))}</td>
    <td>${esc(ou(a.dose))}</td><td>${esc(responsavelDaEtapa(ctx, a))}</td></tr>`).join('');
  return `
    <div class="secao bloco">
      <p class="secao-titulo">CRONOGRAMA DAS ATIVIDADES DE PROTOCOLO HORMONAL</p>
      <table class="lista">
        <thead><tr><th>Data</th><th>Atividade</th><th>Dose</th><th>Responsável</th></tr></thead>
        <tbody>${linhas}</tbody>
      </table>
      <p class="obs-cronograma">Obs.: As atividades acima devem ser realizadas nas
        receptoras selecionadas, sob orientação do médico veterinário da empresa
        prestadora de serviço.</p>
    </div>`;
}

function blocoRecomendacoes(spec) {
  const titulo = spec.recomendacaoAteProxima
    ? 'RECOMENDAÇÕES TÉCNICAS ATÉ A PRÓXIMA VISITA'
    : 'RECOMENDAÇÕES TÉCNICAS:';
  const grupos = RECOMENDACOES.map(g => `
    <p class="rec-titulo">${esc(g.titulo)}</p>
    ${g.linhas.map(l => `<p>${esc(l)}</p>`).join('')}`).join('');
  const extra = spec.recomendacaoAteProxima
    ? `<p>${esc(RECOMENDACAO_SEXAGEM)}</p>` : '';
  return `
    <div class="secao bloco">
      <p class="secao-titulo">${esc(titulo)}</p>
      ${grupos}
      ${extra}
      ${FECHAMENTO.map(f => `<p class="fechamento">${esc(f)}</p>`).join('')}
    </div>`;
}

function blocoAssinatura(ctx) {
  return `
    <div class="assinatura bloco">
      <p>${esc(CIENCIA)}</p>
      <div class="linha-assinatura"></div>
      <p class="nome-produtor">${esc(produtorDoContexto(ctx))}</p>
    </div>`;
}

const CSS_VISITA = `
  /* Sem logo, o cabeçalho é só o nome do laboratório e o código. */
  .page-header { display: block; text-align: center; }
  /* O bloco do laboratório é flex: text-align não o move. Centraliza o flex
     e a linha de dados, que tem largura máxima e ficava presa à esquerda. */
  .page-header .lab-id { justify-content: center; }
  .page-header .lab-dados { margin-left: auto; margin-right: auto; }
  .page-header .client { font-size: 15px; font-weight: 700; }
  .page-header .meta { font-size: 11px; color: #64748b; margin-top: 2px; }
  .titulo { text-align: center; font-size: 15px; font-weight: 700; margin-bottom: 2px; }
  .subtitulo { text-align: center; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 12px; }
  .cabecalho { margin-bottom: 12px; }
  .cabecalho .rot { width: 130px; font-weight: 700; color: #334155; white-space: nowrap; }
  .secao { margin-bottom: 12px; }
  .secao-titulo { font-weight: 700; font-size: 12px; margin-bottom: 4px; }
  .secao p { margin-bottom: 3px; line-height: 1.35; }
  .rec-titulo { font-weight: 700; margin-top: 6px; }
  .fechamento { margin-top: 6px; }
  .texto-livre { white-space: pre-wrap; line-height: 1.4; }
  .lista { margin-bottom: 8px; }
  .lista td { text-align: center; }
  .obs-cronograma { font-size: 10px; color: #64748b; margin-top: 4px; }
  .assinatura { margin-top: 26px; }
  .linha-assinatura { border-bottom: 1px solid #334155; width: 320px; margin: 34px auto 4px; }
  .nome-produtor { text-align: center; font-weight: 600; }`;

/**
 * O documento inteiro, como string. PURA — não toca em `window`.
 *
 * É o que permite testar o conteúdo sem navegador, e o conteúdo aqui é
 * assinado pelo produtor: errar nele custa mais caro que errar numa tela.
 */
export function montarRelatorioHTML(numero, ctx) {
  const spec = RELATORIO_POR_NUMERO[numero];
  if (!spec) return '';

  const sessao = spec.sessaoDo?.(ctx);
  const gravado = ctx?.textos?.[numero] || {};
  const atividades = gravado.atividades || spec.atividadesPadrao?.(ctx) || '';
  const observacoes = gravado.observacoes || spec.observacoesPadrao?.(ctx) || '';

  const aplicacoes = ctx?.protocolo?.aplicacoes || [];
  const cronograma = spec.cronograma === 'todas' ? aplicacoes
    : spec.cronograma === 'retirada' ? aplicacoes.filter(a => a.momento === 'retirada')
    : [];

  return `
<style>${baseCSS()}${CSS_VISITA}</style>
<!-- Sem a logo do FIVPRO: quem emite e assina este documento e o
     LABORATORIO, nao o software que o imprimiu. A logo do produto num
     relatorio de visita seria como a do editor de texto num contrato.
     A do laboratorio, quando configurada, entra. -->
<div class="page-header">
  ${identidadeDoLaboratorio({ nome: ctx?.laboratory?.name, comLogoDoProduto: false })}
  <div class="meta">Serviço ${esc(ctx?.service?.service_code || '—')}</div>
</div>

<p class="titulo">RELATÓRIO DE VISITA ${numero} — ${esc(spec.etapa)}</p>
${spec.subtitulo ? `<p class="subtitulo">${esc(spec.subtitulo)}</p>` : ''}

${cabecalho6Campos(numero, ctx, sessao)}

<div class="secao bloco">
  <p class="secao-titulo">${spec.atividadeNoSingular
    ? 'ATIVIDADE DESENVOLVIDA:' : 'ATIVIDADES DESENVOLVIDAS:'}</p>
  <p class="texto-livre">${esc(atividades)}</p>
</div>

${spec.tabela ? tabelaDeLista({ ...spec.tabela, linhas: spec.tabela.linhas(ctx) }) : ''}
${tabelaCronograma(cronograma, ctx)}
${blocoRecomendacoes(spec)}

<div class="secao bloco">
  <p class="secao-titulo">OBSERVAÇÕES:</p>
  <p class="texto-livre">${esc(observacoes)}</p>
</div>

${blocoAssinatura(ctx)}`;
}

/** Abre a janela de impressão com o relatório montado. */
export function gerarRelatorioDeVisita(numero, ctx) {
  const spec = RELATORIO_POR_NUMERO[numero];
  if (!spec) return;
  const titulo = `Relatorio de visita ${numero} - ${ctx?.service?.client_name || ''}`;
  openPrint(titulo, montarRelatorioHTML(numero, ctx));
}

